import type { AudioRecorderContainerRef } from "@features/session/containers/AudioRecorderContainer";
import { isWeb } from "@helpers/platform";
import { useDevice } from "@hooks/useDevice";
import type {
	BaseItemSchema,
	OriginalItemTypes,
	RenderableItemTypes,
} from "@memorang/types/src/session/new-session";
import { Box, useToast } from "@memorang/ui";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import useAIFeedbackStore from "../../../hooks/useAIFeedbackStore";
import useSessionStore from "../../../hooks/useSessionStore";
import ItemWrapper from "../../ItemWrapper";
import Speaking from "./variants/Speaking";

type Props = {
	item: BaseItemSchema;
	toggleShowReadMore?: () => void;
	originalItemType: OriginalItemTypes;
	mappedItemType: RenderableItemTypes;
};

const SpeakingContainer = ({
	item,
	toggleShowReadMore,
	originalItemType,
	mappedItemType,
}: Props) => {
	const { showToast } = useToast();
	const { t } = useTranslation();
	const userResponse = useAIFeedbackStore((state) => state.userResponse);
	const currentUserResponse = userResponse?.[item.id!];

	const itemStates = useSessionStore((state) => state.itemStates);
	const itemState = itemStates?.get(item.id!);
	const isAnswered = itemState?.answered;

	const { isMobile } = useDevice();

	const audioRecorderContainerRef = useRef<AudioRecorderContainerRef>(null);

	return (
		<ItemWrapper
			item={item}
			toggleShowReadMore={() => {
				if (audioRecorderContainerRef.current?.isRecording) {
					audioRecorderContainerRef.current?.stop();
					showToast({
						message: t("recorder.uiElementPressed"),
					});
				}
				toggleShowReadMore?.();
			}}
			showInstructions
			instructions={item.content?.instruction}
			originalItemType={originalItemType}
			mappedItemType={mappedItemType}
			footer={
				!isWeb && (
					<Box padding={16} paddingBottom={24}>
						<Speaking
							itemId={item.id!}
							currentQuestionAnswered={isAnswered}
							currentUserResponse={currentUserResponse}
							maximumDuration={item.response?.maximumDuration}
							// biome-ignore lint/suspicious/noExplicitAny: container ref type override
							audioRecorderContainerRef={audioRecorderContainerRef as any}
							originalItemType={originalItemType}
						/>
					</Box>
				)
			}
		>
			{isWeb && (
				<Box paddingHorizontal={isMobile ? 16 : 0} paddingBottom={24}>
					<Speaking
						itemId={item.id!}
						currentQuestionAnswered={isAnswered}
						currentUserResponse={currentUserResponse}
						maximumDuration={item.response?.maximumDuration}
						// biome-ignore lint/suspicious/noExplicitAny: container ref type override
						audioRecorderContainerRef={audioRecorderContainerRef as any}
						originalItemType={originalItemType}
					/>
				</Box>
			)}
		</ItemWrapper>
	);
};

export default SpeakingContainer;
